import { CircleDot, Factory, type LucideIcon } from "lucide-react";
import { Tabs as TabsPrimitive } from "radix-ui";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { STATUS_TONE, statusLabel, TONE_TEXT } from "../../lib/status";
import { isOpenTask, lastStationUpdate, MOOD_TEXT, taskMood } from "../../lib/tasks";
import { useFactory } from "../../store";
import type { IssueDetail, IssuesMeta } from "../../types";
import { ColorDot } from "../status-badge";
import { routeSteps } from "../tasks/route-stepper";
import { productionOf, useTaskDetail } from "../tasks/work-order";
import { TimeAgo } from "../time-ago";
import { Skeleton } from "../ui/skeleton";
import { StatusDisc } from "./meta";

export type LifecycleView = "issue" | "factory";

/** One side of the switch: where the issue stands, or where its work order is on the line. */
interface Phase {
  value: LifecycleView;
  title: string;
  icon: LucideIcon;
  /** The status or station, with its marker. */
  lead: ReactNode;
  headline: string;
  detail: ReactNode;
  accent: string | undefined;
  loading: boolean;
}

/**
 * Switches the issue page between the issue and its work order. Each segment carries its own status:
 * the issue's workflow status and how far along the workflow it is, and the work order's station,
 * mood and progress along its route.
 */
export function LifecycleSwitch({
  value,
  onChange,
  issue,
  meta,
  taskId,
}: {
  value: LifecycleView;
  onChange: (value: LifecycleView) => void;
  issue: IssueDetail;
  meta: IssuesMeta | undefined;
  taskId: string;
}) {
  const phases = [useIssuePhase(issue, meta), useFactoryPhase(issue, taskId)];
  return (
    <TabsPrimitive.Root value={value} onValueChange={(next) => onChange(next as LifecycleView)}>
      <TabsPrimitive.List
        aria-label="Lifecycle"
        className="grid grid-cols-2 gap-1 rounded-2xl border bg-muted/50 p-1"
      >
        {phases.map((phase) => (
          <TabsPrimitive.Trigger
            key={phase.value}
            value={phase.value}
            className={cn(
              "group relative flex min-w-0 cursor-pointer flex-col gap-1 overflow-hidden rounded-xl px-4 pt-2.5 pb-3 text-left outline-none transition-[background-color,box-shadow] duration-(--motion-dur) focus-visible:ring-2 focus-visible:ring-ring/60",
              "data-[state=inactive]:hover:bg-background/50",
              "data-[state=active]:bg-background data-[state=active]:shadow-sm",
            )}
          >
            <span className="flex items-center gap-2 text-lg font-semibold tracking-[-0.01em] text-muted-foreground transition-colors group-hover:text-foreground group-data-[state=active]:text-foreground">
              <phase.icon className="size-[18px]" />
              {phase.title}
            </span>
            {phase.loading ? (
              <Skeleton className="h-4 w-32" />
            ) : (
              <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
                {phase.lead}
                <span className="shrink-0 font-medium text-foreground/80">{phase.headline}</span>
                <span className="truncate">· {phase.detail}</span>
              </span>
            )}
            <span
              aria-hidden
              className="absolute inset-x-4 bottom-0 h-[3px] rounded-t-full opacity-0 transition-opacity group-data-[state=active]:opacity-100"
              style={{ background: phase.accent ?? "var(--color-foreground)" }}
            />
          </TabsPrimitive.Trigger>
        ))}
      </TabsPrimitive.List>
    </TabsPrimitive.Root>
  );
}

function useIssuePhase(issue: IssueDetail, meta: IssuesMeta | undefined): Phase {
  const status = meta?.statuses.find((s) => s.key === issue.statusKey);
  const path = (meta?.statuses ?? [])
    .filter((s) => s.category !== "cancelled")
    .sort((a, b) => a.position - b.position);
  const index = path.findIndex((s) => s.key === issue.statusKey);
  return {
    value: "issue",
    title: "Issue",
    icon: CircleDot,
    lead: <StatusDisc category={status?.category} color={status?.color} />,
    headline: status?.name ?? issue.statusKey,
    detail: (
      <>
        {index >= 0 ? `Stage ${index + 1} of ${path.length}` : "Off the workflow"} · updated{" "}
        <TimeAgo at={issue.updatedAt} />
      </>
    ),
    accent: status?.color,
    loading: !meta,
  };
}

function useFactoryPhase(issue: IssueDetail, taskId: string): Phase {
  const { agentById, layout } = useFactory();
  const query = useTaskDetail(taskId);
  const base = { value: "factory" as const, title: "Factory", icon: Factory };
  if (!query.data) {
    return {
      ...base,
      lead: null,
      headline: query.isLoading ? "" : "Unavailable",
      detail: query.isLoading ? null : "The work order could not be loaded",
      accent: undefined,
      loading: query.isLoading,
    };
  }
  const { task, currentRun, mergeQueue } = query.data;
  const { runs, detached, pipeline } = productionOf(query.data, layout, agentById);
  const stationId = issue.currentAgentId ?? task.currentAgentId ?? currentRun?.agentId;
  const station = stationId ? agentById.get(stationId) : undefined;
  const open = isOpenTask(task);
  const stations = routeSteps(task, runs, pipeline, !detached && !task.lane, "").filter(
    (step) => step.id !== "shipped" && step.id !== "lane",
  );
  // A work order parked at a station (awaiting approval, or a person) has finished that run but is still there.
  const done = stations.filter((step) => step.state === "done" && !(open && step.id === stationId)).length;
  const tone = STATUS_TONE[task.status] ?? "neutral";
  const headline =
    task.status === "completed"
      ? "Shipped"
      : task.status === "cancelled"
        ? "Pulled from the line"
        : task.status === "failed"
          ? `Failed${station ? ` at ${station.name}` : ""}`
          : (station?.name ?? "On the line");
  return {
    ...base,
    lead: station && open ? <ColorDot color={station.station.color} className="ring-0" /> : null,
    headline,
    detail: (
      <>
        <span className={TONE_TEXT[tone]}>
          {open
            ? MOOD_TEXT[taskMood(task, currentRun ?? undefined, mergeQueue ?? undefined)]
            : statusLabel(task.status)}
        </span>
        {stations.length > 0 && ` · ${done} of ${stations.length} stations`} · updated{" "}
        <TimeAgo at={lastStationUpdate(task, runs)} />
      </>
    ),
    accent: open ? station?.station.color : undefined,
    loading: false,
  };
}
